/**
 * Multi-signature manager for the Settings page.
 * Lists existing signatures with set-default/edit/delete actions, and offers
 * an "Add Signature" dialog with file upload + label + signatory name + title.
 */

import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Star, Pencil, Trash2, Upload, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormField } from '@/components/forms/FormField';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useSignatures } from '@/composables/shared/useSignatures';
import { signatureService } from '@/services/signature.service';
import type { Signature } from '@/types/table';

const ACCEPT_IMAGES = 'image/png,image/jpeg,image/jpg,image/webp';
const MAX_SIZE = 5 * 1024 * 1024;

const formSchema = z.object({
  label: z.string().min(1, 'Label is required').max(100),
  signatoryName: z.string().min(1, 'Signatory name is required').max(100),
  signatoryTitle: z.string().max(100).optional().or(z.literal('')),
});
type FormData = z.infer<typeof formSchema>;

export function SignaturesManager() {
  const { signatures, isLoading, create, update, replaceFile, setDefault, remove } = useSignatures();
  const [dialogMode, setDialogMode] = useState<'add' | 'edit' | null>(null);
  const [editing, setEditing] = useState<Signature | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Signature | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingFilePreview, setPendingFilePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { label: '', signatoryName: '', signatoryTitle: '' },
  });

  // Reset form when opening dialog
  useEffect(() => {
    if (dialogMode === 'add') {
      form.reset({ label: '', signatoryName: '', signatoryTitle: '' });
      setPendingFile(null);
      if (pendingFilePreview) URL.revokeObjectURL(pendingFilePreview);
      setPendingFilePreview(null);
    } else if (dialogMode === 'edit' && editing) {
      form.reset({
        label: editing.label,
        signatoryName: editing.signatoryName,
        signatoryTitle: editing.signatoryTitle ?? '',
      });
      setPendingFile(null);
      if (pendingFilePreview) URL.revokeObjectURL(pendingFilePreview);
      setPendingFilePreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogMode, editing]);

  const closeDialog = () => {
    setDialogMode(null);
    setEditing(null);
    setPendingFile(null);
    if (pendingFilePreview) { URL.revokeObjectURL(pendingFilePreview); setPendingFilePreview(null); }
  };

  const onPickFile = (f: File | null | undefined) => {
    if (!f) return;
    if (f.size > MAX_SIZE) { alert('Image must be 5 MB or smaller'); return; }
    if (pendingFilePreview) URL.revokeObjectURL(pendingFilePreview);
    setPendingFile(f);
    setPendingFilePreview(URL.createObjectURL(f));
  };

  const onSubmit = (data: FormData) => {
    if (dialogMode === 'add') {
      if (!pendingFile) { alert('Please choose a signature image'); return; }
      create.mutate(
        { file: pendingFile, label: data.label, signatoryName: data.signatoryName, signatoryTitle: data.signatoryTitle, isDefault: signatures.length === 0 },
        { onSuccess: closeDialog },
      );
    } else if (dialogMode === 'edit' && editing) {
      // Update metadata first; replace file separately if user picked a new one
      const tasks: Array<Promise<unknown>> = [];
      tasks.push(update.mutateAsync({ id: editing.id, data: { label: data.label, signatoryName: data.signatoryName, signatoryTitle: data.signatoryTitle } }));
      if (pendingFile) tasks.push(replaceFile.mutateAsync({ id: editing.id, file: pendingFile }));
      Promise.all(tasks).then(closeDialog).catch(() => { /* toasts shown by mutations */ });
    }
  };

  const submitting = create.isPending || update.isPending || replaceFile.isPending;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Signatures</CardTitle>
            <CardDescription>
              Add multiple signatures (each with image + signatory name + title). The default one is used on documents unless overridden at creation.
            </CardDescription>
          </div>
          <Button type="button" size="sm" onClick={() => setDialogMode('add')}>
            <Plus className="mr-2 h-4 w-4" />Add Signature
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-muted-foreground text-sm">Loading…</div>
          ) : signatures.length === 0 ? (
            <div className="text-center text-muted-foreground text-sm py-8">
              No signatures yet. Add your first signature to start using it on invoices and quotations.
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {signatures.map((sig) => (
                <SignatureCard
                  key={sig.id}
                  sig={sig}
                  onSetDefault={() => setDefault.mutate(sig.id)}
                  onEdit={() => { setEditing(sig); setDialogMode('edit'); }}
                  onDelete={() => setConfirmDelete(sig)}
                  busy={setDefault.isPending && setDefault.variables === sig.id}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogMode !== null} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{dialogMode === 'add' ? 'Add Signature' : 'Edit Signature'}</DialogTitle>
            <DialogDescription>
              {dialogMode === 'add'
                ? 'Upload the signature image and fill in who signs it.'
                : 'Update label / name / title, or replace the image.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Image picker */}
            <div className="space-y-2">
              <Label>Image {dialogMode === 'add' && <span className="text-destructive">*</span>}</Label>
              <div className="rounded-md border border-dashed bg-muted/30 h-28 flex items-center justify-center overflow-hidden">
                {pendingFilePreview
                  ? <img src={pendingFilePreview} alt="new signature" className="max-h-full max-w-full object-contain p-2" />
                  : dialogMode === 'edit' && editing
                    ? <img src={signatureService.assetUrl(editing.id, new Date(editing.updatedAt).getTime())} alt={editing.label} className="max-h-full max-w-full object-contain p-2" />
                    : <span className="text-xs text-muted-foreground">No image picked</span>}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_IMAGES}
                className="hidden"
                onChange={(e) => { onPickFile(e.target.files?.[0]); e.target.value = ''; }}
              />
              <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                <Upload className="mr-2 h-3.5 w-3.5" />{dialogMode === 'edit' ? 'Replace Image' : 'Choose Image'}
              </Button>
            </div>

            <FormField form={form} name="label" label="Label" placeholder="e.g. CEO Signature" required />
            <FormField form={form} name="signatoryName" label="Signatory Name" placeholder="e.g. John Smith" required />
            <FormField form={form} name="signatoryTitle" label="Signatory Title" placeholder="e.g. Managing Director" />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving…' : dialogMode === 'add' ? 'Add' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}
        title="Delete Signature?"
        description={`Are you sure you want to delete "${confirmDelete?.label}"? If it's used on any invoice or quotation, the delete will be blocked with a message.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => {
          if (confirmDelete) remove.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) });
        }}
        isLoading={remove.isPending}
      />
    </>
  );
}

interface SignatureCardProps {
  sig: Signature;
  onSetDefault: () => void;
  onEdit: () => void;
  onDelete: () => void;
  busy?: boolean;
}

function SignatureCard({ sig, onSetDefault, onEdit, onDelete, busy }: SignatureCardProps) {
  const cacheBuster = new Date(sig.updatedAt).getTime();
  return (
    <div className="rounded-md border bg-card p-3 space-y-2">
      <div className="rounded bg-muted/30 h-20 flex items-center justify-center overflow-hidden">
        <img src={signatureService.assetUrl(sig.id, cacheBuster)} alt={sig.label} className="max-h-full max-w-full object-contain" />
      </div>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{sig.label}</div>
          <div className="text-xs text-muted-foreground truncate">{sig.signatoryName}{sig.signatoryTitle ? ` · ${sig.signatoryTitle}` : ''}</div>
        </div>
        {sig.isDefault && <Badge variant="default" className="shrink-0"><Check className="h-3 w-3 mr-1" />Default</Badge>}
      </div>
      <div className="flex gap-1">
        {!sig.isDefault && (
          <Button type="button" variant="outline" size="sm" className="flex-1 text-xs" onClick={onSetDefault} disabled={busy}>
            <Star className="mr-1 h-3 w-3" />Set Default
          </Button>
        )}
        <Button type="button" variant="ghost" size="sm" onClick={onEdit}><Pencil className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDelete}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
      </div>
    </div>
  );
}
